/**
 * Azure Speech to Text Helper
 * 替换 Watson Speech SDK
 */

import * as SpeechSDK from 'microsoft-cognitiveservices-speech-sdk';

class AzureSpeechHelper {
  constructor() {
    this.recognizer = null;
    this.audioConfig = null;
    this.speechConfig = null;
    this.isRecognizing = false;
  }

  /**
   * 初始化 Azure Speech 识别器
   * @param {Object} config - 配置对象
   * @param {string} config.subscriptionKey - Azure 订阅密钥
   * @param {string} config.region - Azure 区域
   * @param {string} config.language - 识别语言 (如 'ja-JP')
   * @param {MediaStream} config.mediaStream - 音频流
   * @param {Function} config.onRecognizing - 实时识别回调
   * @param {Function} config.onRecognized - 最终识别回调
   * @param {Function} config.onError - 错误回调
   * @param {boolean} config.continuous - 是否持续识别
   */
  initialize(config) {
    try {
      // 创建 Speech Config
      this.speechConfig = SpeechSDK.SpeechConfig.fromSubscription(
        config.subscriptionKey,
        config.region
      );
      
      // 设置识别语言
      // this.speechConfig.speechRecognitionLanguage = config.language || 'ja-JP';
      this.speechConfig.speechRecognitionLanguage = 'ja-JP';
      this.speechConfig.endpointId = config.endpointId; // 设置自定义端点 ID

      // 如果有自定义端点 ID (类似 Watson 的 customization_id)
      // if (config.customizationId) {
      //   this.speechConfig.endpointId = config.customizationId;
      // }

      // 从 MediaStream 创建音频配置
      if (config.mediaStream) {
        this.audioConfig = SpeechSDK.AudioConfig.fromStreamInput(
          this.createPushStreamFromMediaStream(config.mediaStream)
        );
      } else {
        // 使用默认麦克风
        this.audioConfig = SpeechSDK.AudioConfig.fromDefaultMicrophoneInput();
      }

      // 创建识别器
      this.recognizer = new SpeechSDK.SpeechRecognizer(
        this.speechConfig,
        this.audioConfig
      );

      // 设置事件处理器
      this.setupEventHandlers(config);

      return this;
    } catch (error) {
      console.error('Azure Speech initialize failed:', error);
      if (config.onError) {
        config.onError(error);
      }
      throw error;
    }
  }

  /**
   * 从 MediaStream 创建 PushAudioInputStream
   * Azure Speech SDK 需要 PCM 格式的音频数据
   */
  createPushStreamFromMediaStream(mediaStream) {
    // 创建音频格式：16kHz, 16bit, mono PCM
    const format = SpeechSDK.AudioStreamFormat.getWaveFormatPCM(16000, 16, 1);
    const pushStream = SpeechSDK.AudioInputStream.createPushStream(format);
    
    // 使用 Web Audio API 处理音频流
    const audioContext = new (window.AudioContext || window.webkitAudioContext)({
      sampleRate: 16000 // 设置为16kHz采样率
    });
    
    const source = audioContext.createMediaStreamSource(mediaStream);
    
    // 创建 ScriptProcessor 来处理音频数据
    const scriptProcessor = audioContext.createScriptProcessor(4096, 1, 1);
    
    scriptProcessor.onaudioprocess = (audioProcessingEvent) => {
      const inputBuffer = audioProcessingEvent.inputBuffer;
      const inputData = inputBuffer.getChannelData(0); // 获取单声道数据
      
      // 将 Float32Array 转换为 Int16Array (PCM 16bit)
      const pcmData = new Int16Array(inputData.length);
      for (let i = 0; i < inputData.length; i++) {
        // 将 [-1, 1] 范围的浮点数转换为 [-32768, 32767] 范围的整数
        const s = Math.max(-1, Math.min(1, inputData[i]));
        pcmData[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
      }
      
      // 推送 PCM 数据到 Azure SDK
      pushStream.write(pcmData.buffer);
    };
    
    // 连接音频节点
    source.connect(scriptProcessor);
    scriptProcessor.connect(audioContext.destination);
    
    // 保存引用以便后续停止
    this.audioContext = audioContext;
    this.scriptProcessor = scriptProcessor;
    this.audioSource = source;
    this.pushStream = pushStream;

    console.log('✅ Audio stream created with PCM format (16kHz, 16bit, mono)');
    
    return pushStream;
  }

  /**
   * 设置事件处理器
   */
  setupEventHandlers(config) {
    // 实时识别结果 (对应 Watson 的 interim results)
    this.recognizer.recognizing = (s, e) => {
      if (config.onRecognizing && e.result.text) {
        const result = {
          results: [{
            alternatives: [{
              transcript: e.result.text,
            }],
            final: false,
          }],
        };
        config.onRecognizing(result);
      }
    };

    // 最终识别结果
    this.recognizer.recognized = (s, e) => {
      if (e.result.reason === SpeechSDK.ResultReason.RecognizedSpeech) {
        if (config.onRecognized && e.result.text) {
          const result = {
            results: [{
              alternatives: [{
                transcript: e.result.text,
                timestamps: this.extractTimestamps(e.result),
              }],
              final: true,
            }],
          };
          config.onRecognized(result);
        }
      } else if (e.result.reason === SpeechSDK.ResultReason.NoMatch) {
        console.log('Azure STT: No speech could be recognized.');
      }
    };

    // 错误处理
    this.recognizer.canceled = (s, e) => {
      console.error('Azure STT canceled:', e.errorDetails);
      if (config.onError) {
        config.onError(new Error(e.errorDetails));
      }
      this.stop();
    };

    // 会话结束
    this.recognizer.sessionStopped = (s, e) => {
      this.isRecognizing = false;
    };
  }

  /**
   * 提取时间戳信息 (如果可用)
   */
  extractTimestamps(result) {
    // Azure Speech SDK 可能提供详细的时间戳
    // 这里简化处理，实际使用时可以提取更详细的信息
    return [];
  }

  /**
   * 开始持续识别
   */
  startContinuous() {
    if (!this.recognizer) {
      throw new Error('recognizer is not initialized');
    }
    
    this.isRecognizing = true;
    this.recognizer.startContinuousRecognitionAsync(
      () => {
        console.log('Azure STT recognizer started continuous recognition');
      },
      (error) => {
        console.error('Azure STT start continuous recognition failed:', error);
        this.isRecognizing = false;
      }
    );
  }

  /**
   * 停止识别
   */
  stop() {
    console.log('Azure STT stop this.recognizer:', this.recognizer);
    if (!this.recognizer) {
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      if (this.isRecognizing) {
        this.recognizer.stopContinuousRecognitionAsync(
          () => {
            console.log('Azure STT stopped continuous recognition');
            this.cleanup();
            resolve();
          },
          (error) => {
            console.error('Azure STT stop failed:', error);
            this.cleanup();
            resolve();
          }
        );
      } else {
        this.cleanup();
        resolve();
      }
    });
  }

  /**
   * 清理资源
   */
  cleanup() {
    this.isRecognizing = false;
    
    // 清理 Web Audio API 资源
    if (this.scriptProcessor) {
      try {
        console.log('Disconnecting scriptProcessor');
        this.scriptProcessor.disconnect();
        this.scriptProcessor = null;
      } catch (err) {
        console.warn('Error disconnecting scriptProcessor:', err);
      }
    }
    
    if (this.audioSource) {
      try {
        console.log('Disconnecting audioSource');
        this.audioSource.disconnect();
        this.audioSource = null;
      } catch (err) {
        console.warn('Error disconnecting audioSource:', err);
      }
    }
    
    if (this.audioContext) {
      try {
        console.log('Closing audioContext');
        this.audioContext.close();
        this.audioContext = null;
      } catch (err) {
        console.warn('Error closing audioContext:', err);
      }
    }
    
    // 清理旧的 MediaRecorder（如果存在）
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      try {
        console.log('Stopping mediaRecorder');
        this.mediaRecorder.stop();
      } catch (err) {
        console.warn('Error stopping mediaRecorder:', err);
      }
    }
    
    if (this.pushStream) {
      try {
        this.pushStream.close();
      } catch (err) {
        console.warn('Error closing pushStream:', err);
      }
    }
    
    if (this.recognizer) {
      try {
        this.recognizer.close();
      } catch (err) {
        console.warn('Error closing recognizer:', err);
      }
      this.recognizer = null;
    }
    
    // Don't call audioConfig.close() separately
    // The recognizer.close() already handles audioConfig cleanup
    // Calling it separately causes "Cannot read properties of undefined (reading 'then')" error
    this.audioConfig = null;
    this.speechConfig = null;
  }
}

  /**
   * 模拟 Watson Speech SDK 的接口
   */
  const AzureSpeechSDK = {
    SpeechToText: {
      /**
       * 模拟 WatsonSpeech.SpeechToText.recognizeMicrophone
       * 现在直接使用统一的字段名，不再做映射
       */
      recognizeMicrophone(config) {
        const helper = new AzureSpeechHelper();
        
        // 直接使用标准的 Azure 字段名，不再做任何转换
        // language: config.language || 'ja-JP',

        const azureConfig = {
          subscriptionKey: config.subscriptionKey,
          region: config.region,
          language: 'ja-JP',
          endpointId: config.endpointId,
          mediaStream: config.mediaStream,
          continuous: config.continuous !== false,
          // customizationId: config.customization_id, // 注意：后端返回的是 customization_id (下划线)
          onRecognizing: null,
          onRecognized: null,
          onError: null,
        };
        console.log('Azure STT config:', azureConfig);

      // 创建模拟的 recognizeStream 对象
      const recognizeStream = {
        _messageHandlers: [],
        _initialized: false,
        
        on(event, handler) {
          console.log(`Azure STT register event: ${event}`);
          if (event === 'message') {            
            this._messageHandlers.push(handler);
            
            // 设置 Azure 的事件处理器
            azureConfig.onRecognizing = (result) => {
              const frame = { results: result.results };
              const json = result;
              this._messageHandlers.forEach(h => h(frame, json));
            };
            
            azureConfig.onRecognized = (result) => {
              const frame = { results: result.results };
              const json = result;
              this._messageHandlers.forEach(h => h(frame, json));
            };
            
            azureConfig.onError = (error) => {
              console.error('Azure STT error:', error);
            };
            
            // 只在第一次注册时初始化和启动
            if (!this._initialized) {
              helper.initialize(azureConfig);
              helper.startContinuous();
              this._initialized = true;
            } else {
              console.log('Azure STT recognizeStream already initialized, skipping re-initialization');
            }
          }
          return this;
        },
        
        stop() {
          return helper.stop();
        },
        
        promise() {
          return helper.stop();
        },
      };

      // 返回模拟对象
      return {
        recognizeStream,
        stop: () => helper.stop(),
        promise: () => helper.stop(),
      };
    },
  },
};

export default AzureSpeechSDK;